// コピー / カット / ペースト。
// - 内部バッファ (RGBA + 矩形) を常に持ち、可能ならシステムクリップボードにも PNG を書く
// - ペーストはシステムクリップボードの画像を優先し、無ければ内部バッファ
// - 外部からの paste イベント (画像ファイルの貼り付け) も受ける

import { encodeRgba } from './imageEncode'
import { decodeImageFile } from './imageDecode'
import type { Rect } from '@/types/geometry'

export interface ClipboardImage {
  width: number
  height: number
  rgba: Uint8ClampedArray
  /** コピー元の文書座標 (同じ位置に貼るため)。外部画像なら undefined */
  rect?: Rect
}

let internal: ClipboardImage | null = null

export function getInternalClipboard(): ClipboardImage | null {
  return internal
}

export async function copyImage(img: ClipboardImage): Promise<'system' | 'internal'> {
  internal = img
  try {
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) return 'internal'
    const blob = await encodeRgba(img.width, img.height, img.rgba, { format: 'png' })
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
    return 'system'
  } catch {
    return 'internal'
  }
}

/** システムクリップボードから画像を読む (許可がなければ null) */
export async function readSystemImage(): Promise<ClipboardImage | null> {
  try {
    if (!navigator.clipboard?.read) return null
    const items = await navigator.clipboard.read()
    for (const item of items) {
      const type = item.types.find(t => t.startsWith('image/'))
      if (!type) continue
      const blob = await item.getType(type)
      const d = await decodeImageFile(blob)
      return { width: d.width, height: d.height, rgba: d.rgba }
    }
  } catch {
    /* 権限なし・非対応 */
  }
  return null
}

/** paste イベントの DataTransfer から画像を取り出す */
export async function imageFromDataTransfer(dt: DataTransfer | null): Promise<ClipboardImage | null> {
  if (!dt) return null
  for (const item of dt.items) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (!file) continue
      const d = await decodeImageFile(file)
      return { width: d.width, height: d.height, rgba: d.rgba }
    }
  }
  return null
}
